/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unnecessary-type-assertion */
/**
 * Model optimization using gltf-transform and Canvas texture resampling.
 */

import {
  dedup,
  flatten,
  join,
  prune,
  quantize,
  simplify,
  weld,
} from '@gltf-transform/functions';
import { MeshoptSimplifier } from 'meshoptimizer';
import type {
  AxisConversion,
  MaterialChange,
  OptimizationChangeLog,
  OptimizationProfile,
  OptimizationResult,
  TextureChange,
} from './types';
import { probe } from './probe';
import { readModel } from './modelIO';

// Re-export for back-compat: AxisConversion now lives in types.ts.
export type { AxisConversion } from './types';

/** Per-material edits applied before export. Keyed by material name. */
export interface MaterialOverride {
  /** Force a glTF alpha mode. Omit to keep the authored value. */
  alphaMode?: 'OPAQUE' | 'MASK' | 'BLEND';
  /** Alpha cutoff for MASK mode (0–1). Only used when alphaMode === 'MASK'. */
  alphaCutoff?: number;
  /** Force single/double-sided. Omit to keep the authored value. */
  doubleSided?: boolean;
  /**
   * Re-encode the base-color texture. 'jpeg' drops alpha (use only when the
   * material is OPAQUE); 'keep' leaves the authored format. Texture resampling
   * in the main pass may still apply size limits regardless.
   */
  baseColorFormat?: 'jpeg' | 'png' | 'keep';
}

interface OptimizationOptions {
  profile: OptimizationProfile;
  /** Rotate the model to convert coordinate systems (e.g. Z-up source → Y-up glТF). Default 'none'. */
  axisConversion?: AxisConversion;
  /**
   * Uniform scale factor baked into the geometry (e.g. 0.01 for a
   * centimetre-authored model → metres, glTF's convention). Default 1 (no scale).
   */
  scaleFactor?: number;
  /** Per-material edits (alpha mode, double-sided, base-color format), keyed by material name. */
  materialOverrides?: Record<string, MaterialOverride>;
  onProgress?: (stage: string, ratio: number) => void;
}

/** Default retention ratio when a profile doesn't specify one (keep 90%). */
const DEFAULT_DETAIL = 0.9;

/** The vertex-retention ratio (0–1, 1 = Off) for a profile. */
export function resolveDetail(profile: OptimizationProfile): number {
  return profile.geometryDetail ?? DEFAULT_DETAIL;
}

/**
 * Recommend a geometry-detail retention ratio from cheap probe stats — no trial
 * simplify.
 *
 * The signal is the absolute triangle count against a typical AR budget. This
 * is scale-invariant (unlike per-area density, which a tall/large model or a
 * stray ground plane skews): a single mesh well past ~60k tris is
 * over-tessellated and can shed vertices with little visible loss.
 *
 * Returns the recommended retention ratio (0.5–1.0) plus the over-tessellation
 * factor so the UI can explain the recommendation. We aim the result roughly at
 * the AR triangle budget but never below 50% retention (and bias conservative —
 * the user can always lower the slider for smaller files).
 */
export function recommendDetail(report: {
  geometry: { vertexCount: number; triangleCount: number };
}): { detail: number; overTessellation: number } {
  const AR_TRIANGLE_BUDGET = 60_000;
  const tris = report.geometry.triangleCount;
  const overTessellation = tris / AR_TRIANGLE_BUDGET;

  // Target the AR budget, but floor at 0.5 and keep ≥0.9 when near budget. Round
  // to whole-percent so it lands on a slider notch.
  let detail: number;
  if (overTessellation <= 1.2) detail = 1; // already lean → keep everything
  else detail = Math.max(0.5, Math.min(0.95, 1 / overTessellation));
  detail = Math.round(detail * 100) / 100;

  return { detail, overTessellation };
}

/** Loose simplification ceiling for dense meshes — 1% of mesh radius (the slider max). */
const DEFAULT_ERROR_CEILING = 0.01;

/**
 * Recommend a simplification error ceiling (max surface distortion, a fraction
 * of mesh radius). This caps how far the simplifier may stray from the original
 * surface while chasing the retention target — a tight ceiling protects the
 * smooth panels of a precision/low-poly solid from "divots", while a loose one
 * lets a dense organic mesh reduce freely.
 *
 * Signal: NOT over-tessellated (≤ AR budget) ⇒ the model was authored at its
 * intended density, so deviations are visible → tighten. Over-tessellated ⇒
 * redundant detail, safe to deviate more → loosen.
 */
export function recommendErrorCeiling(report: {
  geometry: { triangleCount: number };
}): number {
  const overTessellation = report.geometry.triangleCount / 60_000;
  if (overTessellation <= 1.2) return 0.001; // 0.1% — precision: protect surfaces
  if (overTessellation <= 4) return 0.005; // 0.5% — moderately dense
  return DEFAULT_ERROR_CEILING; // 1% — dense/organic: reduce freely
}

/**
 * Convert a Z-up model (Blender, 3ds Max, many CAD tools) to glTF's Y-up
 * convention by rotating −90° about the X axis, baked directly into the vertex
 * POSITION/NORMAL/TANGENT data. (A node-level transform doesn't survive the
 * viewer's re-rooting, so we rotate the geometry itself.) For −90° about X:
 * (x, y, z) → (x, z, −y).
 */
function convertZUpToYUp(doc: any): void {
  // Bake a −90° rotation about X directly into vertex POSITION and NORMAL data.
  // (A pivot-node transform doesn't survive flatten()/the viewer's re-rooting,
  // so we rotate the geometry itself.) For −90° about X: (x, y, z) → (x, z, −y).
  const root = doc.getRoot();
  const seen = new Set<unknown>();
  const rotateVec3 = (acc: any): void => {
    if (!acc || seen.has(acc)) return;
    seen.add(acc);
    const a = acc.getArray();
    if (!a) return;
    for (let i = 0; i + 2 < a.length; i += 3) {
      const y = a[i + 1];
      const z = a[i + 2];
      a[i + 1] = z;
      a[i + 2] = -y;
    }
    acc.setArray(a);
  };
  for (const mesh of root.listMeshes()) {
    for (const prim of mesh.listPrimitives()) {
      rotateVec3(prim.getAttribute('POSITION'));
      rotateVec3(prim.getAttribute('NORMAL'));
      // Tangents are VEC4 (xyz + handedness w); rotate the xyz part only.
      const tan = prim.getAttribute('TANGENT');
      if (tan && !seen.has(tan)) {
        seen.add(tan);
        const a = tan.getArray();
        if (a) {
          for (let i = 0; i + 3 < a.length; i += 4) {
            const y = a[i + 1];
            const z = a[i + 2];
            a[i + 1] = z;
            a[i + 2] = -y;
          }
          tan.setArray(a);
        }
      }
    }
  }
}

/**
 * Multiply each scene-root node's scale (and translation) by `factor`, so a
 * later flatten() bakes the user's rescale into the vertices along with any
 * pre-existing node transform. Does NOT flatten itself — callers that need an
 * immediate identity root should follow with `bakeNodeTransforms`.
 */
function scaleRootNodes(doc: any, factor: number): void {
  if (factor === 1) return;
  const root = doc.getRoot();
  for (const scene of root.listScenes()) {
    for (const node of scene.listChildren()) {
      const s = node.getScale();
      node.setScale([s[0] * factor, s[1] * factor, s[2] * factor]);
      const t = node.getTranslation();
      node.setTranslation([t[0] * factor, t[1] * factor, t[2] * factor]);
    }
  }
}

/**
 * Bake every node's TRS into the vertices, leaving nodes at identity.
 *
 * We want the optimized model's root at unit scale (no rotation/translation)
 * because many sources carry a non-trivial root transform (Unity exports a 100×
 * "GameObject" wrapper), and a downstream tool or re-import may collapse,
 * re-root, or ignore node TRS — so the durable place for "how big is this
 * model" is the vertex data itself. flatten() composes the full hierarchy into
 * the geometry, so a single-root model ends with a clean unit root and its true
 * world size in the positions.
 */
async function bakeNodeTransforms(doc: any): Promise<void> {
  await doc.transform(flatten());
}

/**
 * Apply only an axis conversion and/or uniform rescale to a GLB, returning a
 * new GLB blob. Used by the editor preview so the "before" pane can show the
 * source in the same orientation and scale the optimized output will have.
 * Returns the input unchanged when there's nothing to do. No geometry
 * optimization is performed. When any transform is applied, node TRS is baked
 * into the vertices via flatten() — matching optimize()'s output so the
 * before/after panes agree on world-space size.
 */
export async function applyAxisConversion(
  input: File | Blob,
  conversion: AxisConversion,
  scaleFactor = 1,
): Promise<Blob> {
  if (conversion === 'none' && scaleFactor === 1) {
    return input instanceof Blob ? input : new Blob([input]);
  }
  const { io, doc } = await readModel(await input.arrayBuffer());
  if (conversion === 'zUpToYUp') convertZUpToYUp(doc);
  scaleRootNodes(doc, scaleFactor);
  // Bake to an identity root so the preview's "before" pane matches the
  // optimized output (which always flattens), keeping the comparison faithful.
  // Always bake here — even for a pure axis conversion (scaleFactor === 1) the
  // source may carry a pre-existing node TRS (e.g. a Unity 100× root) that
  // optimize() flattens, so skipping the bake would leave the panes at
  // different world-space sizes. The no-op case already returned above.
  await bakeNodeTransforms(doc);
  const out = await io.writeBinary(doc);
  return new Blob([out as BlobPart], { type: 'model/gltf-binary' });
}

export async function optimize(
  input: File | Blob,
  options: OptimizationOptions,
): Promise<OptimizationResult> {
  const originalSize = input.size;
  const originalBuffer = await input.arrayBuffer();
  const originalReport = await probe(input);

  const { io, doc, decoded } = await readModel(originalBuffer);
  const root = (doc as any).getRoot();

  // Record each gltf-transform / meshopt call (with args) for a transparent
  // "what was actually run" log.
  const pipeline: string[] = [];
  for (const label of decoded) pipeline.push(`decode ${label} — geometry written uncompressed`);

  // Ensure a scene is marked active. A glTF with scenes but no top-level `scene`
  // index is ambiguous — some runtimes render nothing — so default to the first.
  let setActiveScene = false;
  if (root.getDefaultScene() == null) {
    const scenes = root.listScenes();
    if (scenes.length > 0) {
      root.setDefaultScene(scenes[0]);
      setActiveScene = true;
      pipeline.push('setDefaultScene(scene[0]) — no active scene was set');
    }
  }

  // Axis conversion (e.g. Z-up authoring tools → glTF's Y-up convention).
  // Done first so all later geometry transforms operate in final orientation.
  if (options.axisConversion === 'zUpToYUp') {
    options.onProgress?.('Converting axes', 0.05);
    convertZUpToYUp(doc);
    pipeline.push('convertZUpToYUp() — rotate vertices −90° about X');
  }

  // Uniform rescale (e.g. centimetre-authored model → metres for AR). We scale
  // the root nodes here; the pipeline's later flatten() bakes the factor — and
  // any pre-existing root transform — into the vertices, leaving a unit root.
  const scaleFactor = options.scaleFactor ?? 1;
  if (scaleFactor !== 1) {
    options.onProgress?.('Rescaling', 0.06);
    scaleRootNodes(doc, scaleFactor);
    pipeline.push(`scaleRootNodes(×${scaleFactor}) — scale roots, baked to unit root by flatten()`);
  }

  // Apply per-material edits (alpha mode, double-sided, base-color format).
  options.onProgress?.('Applying material edits', 0.08);
  const materialChanges = applyMaterialOverrides(doc, options.materialOverrides);

  options.onProgress?.('Deduplicating', 0.1);
  await doc.transform(dedup());
  pipeline.push('dedup()');

  options.onProgress?.('Welding', 0.2);
  await doc.transform(weld());
  pipeline.push('weld()');

  options.onProgress?.('Pruning', 0.3);
  await doc.transform(prune());
  pipeline.push('prune()');

  // Mesh simplification is RETENTION-driven: keep `geometryDetail` of the
  // vertices. Predictable (90% detail ≈ keeps 90%) and avoids the "divot cliff"
  // of error-driven simplification. The error acts only as a generous quality
  // ceiling so the ratio target governs. 1 = Off (skip simplification).
  options.onProgress?.('Simplifying geometry', 0.5);
  const retentionRatio = resolveDetail(options.profile);
  // Quality ceiling: how far the simplifier may deviate from the original
  // surface. Tight on precision meshes (protects smooth panels from divots),
  // loose on dense organic ones. Explicit profile value wins; otherwise the
  // per-mesh recommendation.
  const errorCeiling = options.profile.errorCeiling
    ?? recommendErrorCeiling(originalReport);

  if (retentionRatio < 1) {
    await doc.transform(
      simplify({
        simplifier: MeshoptSimplifier,
        ratio: retentionRatio,
        error: errorCeiling,
      }),
    );
    pipeline.push(`simplify({ ratio: ${retentionRatio}, error: ${errorCeiling} })`);
  } else {
    pipeline.push('simplify — skipped (geometry detail 100%)');
  }

  // Resample textures to max size and preferred format
  options.onProgress?.('Resampling textures', 0.65);
  const textureChanges = await resampleTextures(doc, options.profile, options.materialOverrides);
  // Only log the resample step when the model actually had textures to process —
  // otherwise the "what was run" log shows a no-op step for texture-free models.
  if (textureChanges.length > 0) {
    pipeline.push(
      `resampleTextures({ maxSide: ${options.profile.maxTextureSideLength}px, `
      + `jpegQuality: ${options.profile.jpgQuality}%, color→JPEG, data maps→PNG })`,
    );
  }

  // Flatten and join to reduce draw calls
  options.onProgress?.('Flattening', 0.8);
  await doc.transform(flatten());
  await doc.transform(join());
  pipeline.push('flatten()', 'join()');

  // Quantize last: join() merges primitives into float attributes, which undoes
  // an earlier quantize().
  if (options.profile.quantize) {
    options.onProgress?.('Quantizing', 0.9);
    await doc.transform(
      quantize({
        quantizePosition: 14,
        quantizeNormal: 8,
        quantizeTexcoord: 10,
      }),
    );
    pipeline.push('quantize({ quantizePosition: 14, quantizeNormal: 8, quantizeTexcoord: 10 })');
  }

  options.onProgress?.('Encoding', 0.95);
  const optimizedBuffer = await io.writeBinary(doc);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const blob = new Blob([optimizedBuffer], { type: 'model/gltf-binary' });
  const bytesSaved = originalSize - blob.size;
  const percentReduced = Math.round((bytesSaved / originalSize) * 100);

  // Get optimized report
  const optimizedReport = await probe(blob);

  const basename = input instanceof File ? input.name.replace(/\.[^.]+$/, '') : 'model';
  const filename = `${basename}.optimized.glb`;

  const changes: OptimizationChangeLog = {
    vertexBefore: originalReport.geometry.vertexCount,
    vertexAfter: optimizedReport.geometry.vertexCount,
    triangleBefore: Math.round(originalReport.geometry.triangleCount),
    triangleAfter: Math.round(optimizedReport.geometry.triangleCount),
    geometryBytesBefore: originalReport.geometry.sizeBytes ?? 0,
    geometryBytesAfter: optimizedReport.geometry.sizeBytes ?? 0,
    quantized: options.profile.quantize,
    axisConversion: options.axisConversion ?? 'none',
    scaleFactor,
    setActiveScene,
    textures: textureChanges,
    materials: materialChanges,
    pipeline,
  };

  return {
    blob,
    filename,
    report: optimizedReport,
    bytesSaved,
    percentReduced,
    changes,
  };
}

/**
 * Apply per-material edits to the document. Sets alpha mode (+ cutoff),
 * double-sided, and base-color texture format per `materialOverrides`. Mutating
 * the material's alphaMode here also lets resampleTextures keep alpha only where
 * it's still needed. Unknown material names and empty overrides are no-ops.
 */
function applyMaterialOverrides(
  doc: any,
  overrides: Record<string, MaterialOverride> | undefined,
): MaterialChange[] {
  if (!overrides) return [];
  const root = doc.getRoot() as any;
  const changes: MaterialChange[] = [];
  for (const material of root.listMaterials() as any) {
    const name = material.getName();
    const ov = name ? overrides[name] : undefined;
    if (!ov) continue;

    const edits: string[] = [];
    if (ov.alphaMode && ov.alphaMode !== material.getAlphaMode()) {
      edits.push(`${material.getAlphaMode()} → ${ov.alphaMode} alpha`);
      material.setAlphaMode(ov.alphaMode);
    }
    if (ov.alphaMode === 'MASK' && ov.alphaCutoff !== undefined) {
      material.setAlphaCutoff(ov.alphaCutoff);
    }
    if (ov.doubleSided !== undefined && ov.doubleSided !== material.getDoubleSided()) {
      edits.push(ov.doubleSided ? 'enabled double-sided' : 'disabled double-sided');
      material.setDoubleSided(ov.doubleSided);
    }
    if (ov.baseColorFormat && ov.baseColorFormat !== 'keep') {
      edits.push(`base color → ${ov.baseColorFormat.toUpperCase()}`);
    }
    // baseColorFormat is honored in resampleTextures (it owns re-encoding) via
    // forcedBaseColorFormats(). A forced 'jpeg' is suppressed there if the
    // material still needs alpha (MASK/BLEND), so pair it with alphaMode:
    // 'OPAQUE' to guarantee the conversion takes effect.
    if (edits.length) changes.push({ name: name as string, edits });
  }
  return changes;
}

/**
 * Decide whether a base-color texture still needs an alpha channel: true if any
 * material that binds it as base color uses MASK or BLEND. Other texture slots
 * (normal, ORM, emissive) never need alpha, so they can always be JPEG.
 */
function baseColorTexturesNeedingAlpha(doc: any): Set<unknown> {
  const root = doc.getRoot() as any;
  const needsAlpha = new Set<unknown>();
  for (const material of root.listMaterials() as any) {
    const tex = material.getBaseColorTexture();
    if (!tex) continue;
    const mode = material.getAlphaMode();
    if (mode === 'MASK' || mode === 'BLEND') needsAlpha.add(tex);
  }
  return needsAlpha;
}

/**
 * Collect the "color-role" textures — base color and emissive. Only these are
 * perceptual color maps that tolerate JPEG. Data maps (normal, metallic-
 * roughness, occlusion) encode geometry/material values in their channels and
 * are destroyed by JPEG's DCT + chroma subsampling (normal maps especially turn
 * to surface noise), so they keep their original lossless format.
 */
function colorRoleTextures(doc: any): Set<unknown> {
  const root = doc.getRoot() as any;
  const color = new Set<unknown>();
  for (const material of root.listMaterials() as any) {
    const base = material.getBaseColorTexture();
    if (base) color.add(base);
    const emissive = material.getEmissiveTexture();
    if (emissive) color.add(emissive);
  }
  return color;
}

/** Short format token for display: image/jpeg → 'jpeg'. */
function formatToken(mime: string): string {
  return (mime.split('/')[1] ?? mime).toLowerCase();
}

/**
 * Map each base-color texture to a format forced by a material override
 * (`baseColorFormat: 'jpeg' | 'png'`). `'keep'`/absent overrides contribute
 * nothing. resampleTextures consults this so an explicit per-material format
 * choice is actually honored (not just logged) — but a forced JPEG is still
 * suppressed for textures that must keep alpha, since JPEG has no alpha channel.
 */
export function forcedBaseColorFormats(
  doc: any,
  overrides: Record<string, MaterialOverride> | undefined,
): Map<unknown, 'image/jpeg' | 'image/png'> {
  const forced = new Map<unknown, 'image/jpeg' | 'image/png'>();
  if (!overrides) return forced;
  const root = doc.getRoot() as any;
  for (const material of root.listMaterials() as any) {
    const name = material.getName() as string;
    const fmt = name ? overrides[name]?.baseColorFormat : undefined;
    if (!fmt || fmt === 'keep') continue;
    const tex = material.getBaseColorTexture();
    if (!tex) continue;
    forced.set(tex, fmt === 'jpeg' ? 'image/jpeg' : 'image/png');
  }
  return forced;
}

async function resampleTextures(
  doc: any,
  profile: OptimizationProfile,
  materialOverrides?: Record<string, MaterialOverride>,
): Promise<TextureChange[]> {
  const root = doc.getRoot() as any;
  const maxSide = profile.maxTextureSideLength;
  const jpgQuality = profile.jpgQuality;
  const alphaTextures = baseColorTexturesNeedingAlpha(doc);
  const colorTextures = colorRoleTextures(doc);
  const forcedFormats = forcedBaseColorFormats(doc, materialOverrides);
  const changes: TextureChange[] = [];
  let textureCount = 0;

  for (const texture of root.listTextures() as any) {
    const image = texture.getImage();
    if (!image) continue;

    // 1-based fallback to match the probe's texture naming (the Textures panel).
    textureCount++;
    const name = (texture.getName() as string) || `texture-${textureCount}`;
    const fromBytes = (image as Uint8Array).byteLength;
    const fromMime = texture.getMimeType() as string;

    // Decode image
    const blob = new Blob([image]);
    const bitmap = await createImageBitmap(blob);
    const fromWidth = bitmap.width;
    const fromHeight = bitmap.height;

    // Calculate new size (maintain aspect ratio, max side = maxSide)
    let width = bitmap.width;
    let height = bitmap.height;
    const maxDim = Math.max(width, height);
    const needsResize = maxDim > maxSide;
    if (needsResize) {
      const ratio = maxSide / maxDim;
      width = Math.floor(width * ratio);
      height = Math.floor(height * ratio);
    }

    // Per-texture format decision. The guiding rule is: KEEP the current format
    // unless there's a clear win to switch. The only beneficial conversion is a
    // color-role PNG with no alpha → JPEG (smaller, no visible loss). We never
    // force PNG onto an existing JPEG — that bloats the file (a 33 KB JPEG can
    // become a 600 KB PNG) and recovers no quality the JPEG already lost.
    const currentMime = fromMime;
    const alreadyJpeg = currentMime === 'image/jpeg';
    const mustKeepAlpha = alphaTextures.has(texture);
    const isColorRole = colorTextures.has(texture);

    // An explicit per-material baseColorFormat override wins over the automatic
    // rule, except a forced JPEG is ignored when the texture must keep alpha
    // (JPEG has no alpha channel — silently dropping it would be a regression).
    const forced = forcedFormats.get(texture);
    const forceJpeg = forced === 'image/jpeg' && !mustKeepAlpha;
    const forcePng = forced === 'image/png';

    // Convert PNG → JPEG only for a color map that carries no alpha.
    const convertToJpeg =
      profile.preferJpg && isColorRole && !mustKeepAlpha && currentMime === 'image/png';
    // Output JPEG if forced, if we're converting, or if it's already JPEG
    // (preserve format) — unless an override explicitly forces PNG.
    const useJpg = forcePng ? false : (forceJpeg || convertToJpeg || alreadyJpeg);
    const targetMime = useJpg ? 'image/jpeg' : 'image/png';

    const recordUnchanged = (): void => {
      changes.push({
        name,
        fromWidth, fromHeight, toWidth: fromWidth, toHeight: fromHeight,
        fromFormat: formatToken(fromMime), toFormat: formatToken(fromMime),
        fromBytes, toBytes: fromBytes, changed: false,
      });
    };

    // Nothing to do if not resizing and the format already matches the target.
    const formatUnchanged = currentMime === targetMime;
    if (!needsResize && formatUnchanged) {
      recordUnchanged();
      bitmap.close();
      continue;
    }

    // Resample via OffscreenCanvas
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(bitmap, 0, 0, width, height);

    const mimeType = targetMime;
    const quality = useJpg ? jpgQuality / 100 : undefined;
    const resampledBlob = await canvas.convertToBlob({
      type: mimeType,
      quality,
    });

    // Update texture in document
    const resampledArray = new Uint8Array(await resampledBlob.arrayBuffer());
    texture.setImage(resampledArray);
    texture.setMimeType(mimeType);

    changes.push({
      name,
      fromWidth, fromHeight, toWidth: width, toHeight: height,
      fromFormat: formatToken(fromMime), toFormat: formatToken(mimeType),
      fromBytes, toBytes: resampledArray.byteLength, changed: true,
    });

    bitmap.close();
  }
  return changes;
}
